package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input
import org.w3c.files.File
import org.w3c.files.get

/** Keeps native byte counts available for progress semantics rather than parsing formatted copy. */
internal data class BackgroundUploadProgress(val sent: Long, val total: Long)

/** Artwork stays a bounded PNG/JPEG upload; the server remains authoritative about its bytes. */
@Composable
internal fun BackgroundPicker(
    canWrite: Boolean, image: Boolean, scrim: Boolean, uploading: Boolean, progress: BackgroundUploadProgress?,
    onFill: (Paint) -> Unit, onUpload: (File) -> Unit, onScrim: (Boolean) -> Unit,
) {
    Column(Modifier.gap(Space.MD)) {
        LSectionHeading(t("app.projects.slug.screenshots.background_picker.background", "Background"), description = t("app.projects.slug.screenshots.background_picker.use_palette_colours_or_your_own", "Use palette colours or your own image."))
        if (canWrite) {
            Row(Modifier.gap(Space.SM)) {
                LButton(t("app.projects.slug.screenshots.background_picker.fill", "Palette fill"), variant = LButtonVariant.SECONDARY, onClick = { onFill(Paint.Solid("@bg")) })
                LButton(t("app.projects.slug.screenshots.background_picker.gradient", "Gradient"), variant = LButtonVariant.SECONDARY,
                    onClick = { onFill(Paint.Linear(160.0, listOf(Stop(0.0, "@bg"), Stop(1.0, "@accent")))) })
            }
            if (!uploading) Input(InputType.File, attrs = {
                attr("accept", "image/png,image/jpeg")
                attr("aria-label", tr("app.projects.slug.screenshots.background_picker.upload", "Upload a background image"))
                style { property("min-height", "40px"); property("max-width", "100%") }
                onChange { event -> event.target.files?.get(0)?.let(onUpload); event.target.value = "" }
            })
            LMuted(t("app.projects.slug.screenshots.background_picker.limit", "PNG or JPEG, up to 8 MiB."))
            if (image) LButton(if (scrim) t("app.projects.slug.screenshots.background_picker.remove_scrim", "Remove scrim")
                else t("app.projects.slug.screenshots.background_picker.add_scrim", "Add scrim"), variant = LButtonVariant.SECONDARY, onClick = { onScrim(!scrim) })
        } else LMuted(if (image) t("app.projects.slug.screenshots.background_picker.own", "Uploaded background image")
            else t("app.projects.slug.screenshots.background_picker.palette", "Palette background"))
        if (uploading) LTaskProgress(
            done = progress?.sent?.coerceIn(0L, Int.MAX_VALUE.toLong())?.toInt() ?: 0,
            total = progress?.total?.coerceIn(0L, Int.MAX_VALUE.toLong())?.toInt() ?: 0,
            label = t("app.projects.slug.screenshots.background_picker.progress", "Background image upload"),
            indeterminate = progress == null || progress.total <= 0,
            detail = progress?.let { tf("app.projects.slug.screenshots.background_picker.bytes", "Uploaded {done} of {total} bytes.", "done" to it.sent, "total" to it.total) },
        )
    }
}
